/**
 * RxScanReviewModal — the "We found these fields. Confirm or correct them." step.
 *
 * Sits between the OCR/barcode scan and saving the prescription. Every
 * scanner-filled field is rendered as editable with a confidence chip:
 *   - 0.80–1.00 → "SCANNED" (green)
 *   - 0.50–0.79 → "REVIEW"  (amber)
 *   - 0.00–0.49 → "LOW"     (red — manual review required)
 * The user can edit any field, then click "Confirm and continue" to
 * pass the corrected payload back up to the form. A "Learn from this
 * correction" button optionally pushes the confirmed payload to the
 * pharmacy-pattern store so the next scan from the same pharmacy gets
 * better hints.
 *
 * NDC lookup is opt-in: if the decoded barcode classifies as "ndc", a
 * "Look up in FDA NDC Directory" button appears. Clicking it fills any
 * empty Medication / Strength fields with the FDA values (never overrides
 * existing user text).
 *
 * Privacy: the image is NEVER passed into this component — it lives only
 * inside the file-pick scope and is GC'd as soon as the OCR request returns.
 */
import React, { useMemo, useState } from "react";
import {
  X, ShieldCheck, Barcode, FileText, Sparkle, MagnifyingGlass, CheckCircle,
  Warning, WarningCircle, Brain, Lightning,
} from "@phosphor-icons/react";
import { classifyBarcode } from "@/lib/barcodeScanner";
import { lookupNdc, learnPharmacyPattern } from "@/lib/api";
import { showToast } from "@/lib/toast";

// Field display order + labels
const FIELD_ROWS = [
  { id: "medName",          label: "Medication name",        placeholder: "Ibuprofen" },
  { id: "strength",         label: "Strength / dose",        placeholder: "600 mg" },
  { id: "directions",       label: "Directions (SIG)",       placeholder: "Take 1 tablet by mouth three times a day with food" },
  { id: "prescriber",       label: "Prescriber",             placeholder: "Dr. Smith, VAMC Greensboro" },
  { id: "pharmacy",         label: "Pharmacy",               placeholder: "NMR/TU Atsugi" },
  { id: "rxNumber",         label: "Rx number",              placeholder: "RX9988123" },
  { id: "quantity",         label: "Quantity",               placeholder: "30 tablets" },
  { id: "lastFilled",       label: "Date filled (ISO)",      placeholder: "2026-02-15", type: "date" },
  { id: "daysSupply",       label: "Days supply",            placeholder: "30", type: "number" },
  { id: "refillsRemaining", label: "Refills remaining",      placeholder: "0", type: "number" },
  { id: "notes",            label: "Notes / warnings",       placeholder: "Take with food. May cause drowsiness." },
];

function confidenceTier(score) {
  if (score == null) return null;
  if (score >= 0.80) return { tier: "scanned", label: "SCANNED",  color: "#047857", bg: "rgba(16,185,129,0.10)",  border: "rgba(16,185,129,0.32)" };
  if (score >= 0.50) return { tier: "review",  label: "REVIEW",   color: "#b45309", bg: "rgba(217,119,6,0.10)",   border: "rgba(217,119,6,0.32)"  };
  if (score > 0)     return { tier: "low",     label: "LOW",      color: "#b91c1c", bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.32)"  };
  return null;
}

export default function RxScanReviewModal({
  initial, barcode, barcodeFormat, barcodeSource, confidence, onCancel, onConfirm,
}) {
  // Local editable copy of the OCR-filled fields. The user can correct any field.
  const [form, setForm] = useState(() => ({
    medName: initial?.medName || "",
    strength: initial?.strength || "",
    directions: initial?.directions || "",
    prescriber: initial?.prescriber || "",
    pharmacy: initial?.pharmacy || "",
    rxNumber: initial?.rxNumber || "",
    quantity: initial?.quantity || "",
    lastFilled: initial?.lastFilled || "",
    daysSupply: initial?.daysSupply || 0,
    refillsRemaining: initial?.refillsRemaining || 0,
    notes: initial?.notes || "",
  }));
  const [learn, setLearn] = useState(true);  // "Learn from this correction" default ON
  const [ndcStatus, setNdcStatus] = useState("");
  const [ndcResult, setNdcResult] = useState(null);

  function patch(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  const barcodeKind = useMemo(() => (barcode ? classifyBarcode(barcode) : "internal"), [barcode]);

  async function handleNdcLookup() {
    if (!barcode) return;
    setNdcStatus("Looking up…");
    setNdcResult(null);
    try {
      const r = await lookupNdc(barcode);
      setNdcResult(r);
      setNdcStatus("");
      // Auto-fill empty fields ONLY — never override what the vet already has.
      setForm((p) => ({
        ...p,
        medName: p.medName || r.brand_name || r.generic_name || "",
        strength: p.strength || r.strength || "",
      }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "NDC lookup failed.";
      setNdcStatus(msg);
    }
  }

  // Fields the user has changed away from the OCR value count as "confirmed".
  // Fields they leave as-is are marked "needs confirmation" so the saved record
  // remembers they came from the scanner.
  function computeScannedFields() {
    const out = [];
    if (!initial) return out;
    for (const row of FIELD_ROWS) {
      const orig = initial[row.id];
      const now = form[row.id];
      if (orig && orig === now) out.push(row.id);
    }
    return out;
  }

  async function handleConfirm() {
    const scannedFields = computeScannedFields();
    const payload = {
      ...form,
      barcodeData: barcode || "",
      barcodeFormat: barcodeFormat || "",
      scanConfidence: confidence || {},
      scannedFields,
      learnedFromPharmacy: form.pharmacy || "",
    };
    // Fire-and-forget the learn step. We don't block save on it.
    if (learn && form.pharmacy && form.pharmacy.trim()) {
      learnPharmacyPattern(form.pharmacy.trim(), {
        medName: form.medName || "",
        strength: form.strength || "",
        directions: form.directions || "",
        rxNumber: form.rxNumber || "",
        quantity: form.quantity || "",
        daysSupply: Number(form.daysSupply) || 0,
        refillsRemaining: Number(form.refillsRemaining) || 0,
        barcodeFormat: barcodeFormat || "",
      })
        .then(() => { showToast({ variant: "success", message: `Learned pattern for "${form.pharmacy}". Future scans from this pharmacy will be smarter.`, duration: 4000 }); })
        .catch(() => { showToast({ variant: "error", message: "Couldn't save the pharmacy pattern — your prescription was still saved.", duration: 3500 }); });
    }
    onConfirm(payload);
  }

  // Status chips for the header summary
  const headerStatus = useMemo(() => {
    const chips = [];
    if (barcode) chips.push({ id: "barcode", label: "BARCODE DECODED", color: "#047857", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.32)", icon: Barcode });
    const anyFilled = FIELD_ROWS.some((r) => initial?.[r.id]);
    if (anyFilled) chips.push({ id: "ocr", label: "OCR TEXT FOUND", color: "#047857", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.32)", icon: FileText });
    chips.push({ id: "review", label: "NEEDS USER CONFIRMATION", color: "#b45309", bg: "rgba(217,119,6,0.10)", border: "rgba(217,119,6,0.32)", icon: WarningCircle });
    // Low-confidence check
    const hasLow = Object.values(confidence || {}).some((v) => v > 0 && v < 0.5);
    if (hasLow) chips.push({ id: "low", label: "LOW CONFIDENCE — REVIEW MANUALLY", color: "#b91c1c", bg: "rgba(220,38,38,0.10)", border: "rgba(220,38,38,0.32)", icon: Warning });
    return chips;
  }, [barcode, initial, confidence]);

  return (
    <div
      data-testid="rx-scan-review-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm scanned prescription fields"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1200,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        padding: 16,
      }}
    >
      <div style={{
        background: "var(--surface, #fff)", borderRadius: 16,
        boxShadow: "0 24px 60px rgba(15,23,42,0.30)",
        width: "min(820px, 96vw)", maxHeight: "94vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", background: "linear-gradient(135deg, rgba(31,58,107,0.06), rgba(31,58,107,0.02))" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Sparkle size={18} weight="duotone" color="#1F3A6B" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>
                Review scanned prescription
              </div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                We found these fields. Confirm or correct them before saving. The label image was not stored.
              </div>
            </div>
            <button
              type="button"
              data-testid="rx-scan-review-close"
              onClick={onCancel}
              aria-label="Close"
              style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, borderRadius: 8 }}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="rx-scan-status-chips">
            {headerStatus.map((c) => {
              const Icon = c.icon;
              return (
                <span
                  key={c.id}
                  data-testid={`rx-scan-status-${c.id}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px",
                    background: c.bg,
                    border: `1px solid ${c.border}`,
                    color: c.color,
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                  }}
                >
                  <Icon size={10} weight="bold" /> {c.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Barcode panel */}
        {barcode && (
          <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--border)", background: "var(--surface-2, #F8FAFC)" }} data-testid="rx-scan-barcode-panel">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Barcode size={14} weight="bold" color="#1F3A6B" />
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", color: "var(--text-primary)" }}>
                DECODED BARCODE VALUE
              </span>
              {barcodeFormat && <span className="mono" style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 999, background: "rgba(31,58,107,0.10)", border: "1px solid rgba(31,58,107,0.30)", color: "#1F3A6B", fontWeight: 700 }}>
                {barcodeFormat.replace(/_/g, " ")}
              </span>}
              {barcodeSource && <span className="mono" style={{ fontSize: 9.5, color: "var(--text-secondary)" }}>via {barcodeSource}</span>}
              <span style={{ marginLeft: "auto" }} className="mono" data-testid="rx-scan-barcode-kind">
                <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-secondary)", letterSpacing: "0.06em" }}>
                  LIKELY: {barcodeKind.toUpperCase().replace(/-/g, " ")}
                </span>
              </span>
            </div>
            <div data-testid="rx-scan-barcode-value" className="mono" style={{
              padding: "6px 10px", background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 6, fontSize: 12, color: "var(--text-primary)", wordBreak: "break-all",
            }}>
              {barcode}
            </div>
            {barcodeKind === "ndc" && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn--outline btn--sm"
                  onClick={handleNdcLookup}
                  data-testid="rx-scan-ndc-lookup"
                >
                  <MagnifyingGlass size={11} weight="bold" /> Look up in FDA NDC Directory
                </button>
                {ndcStatus && <span data-testid="rx-scan-ndc-status" style={{ marginLeft: 8, fontSize: 11, color: "var(--text-secondary)" }}>{ndcStatus}</span>}
                {ndcResult && (
                  <div data-testid="rx-scan-ndc-result" style={{ marginTop: 6, padding: "6px 10px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.30)", borderRadius: 8, fontSize: 11.5, color: "var(--text-primary)" }}>
                    <strong>{ndcResult.brand_name || ndcResult.generic_name}</strong> ({ndcResult.generic_name}) — {ndcResult.dosage_form}, {ndcResult.strength}
                  </div>
                )}
              </div>
            )}
            {barcodeKind === "internal" && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-secondary)", fontStyle: "italic" }}>
                Internal pharmacy code — saved as raw barcode data. Not forced into NDC or Rx number.
              </div>
            )}
          </div>
        )}

        {/* Field grid */}
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {FIELD_ROWS.map((row) => {
              const conf = confidence?.[row.id];
              const tier = confidenceTier(conf);
              return (
                <label key={row.id} style={{ display: "block" }} data-testid={`rx-scan-row-${row.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 800, color: "var(--text-secondary)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                      {row.label}
                    </span>
                    {tier && (
                      <span
                        data-testid={`rx-scan-conf-${row.id}`}
                        className="mono"
                        title={`OCR confidence ${(conf * 100).toFixed(0)}%`}
                        style={{
                          fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
                          padding: "1px 6px", borderRadius: 999,
                          background: tier.bg, border: `1px solid ${tier.border}`, color: tier.color,
                        }}
                      >
                        {tier.label}
                      </span>
                    )}
                  </div>
                  <input
                    className="field-input"
                    type={row.type || "text"}
                    placeholder={row.placeholder}
                    value={form[row.id] ?? ""}
                    onChange={(e) => patch(row.id, row.type === "number" ? Number(e.target.value) : e.target.value)}
                    data-testid={`rx-scan-input-${row.id}`}
                  />
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", background: "var(--surface-2, #F8FAFC)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-primary)", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={learn}
              onChange={(e) => setLearn(e.target.checked)}
              data-testid="rx-scan-learn-toggle"
            />
            <Brain size={13} weight="duotone" color="#1F3A6B" />
            <span>Learn from this correction</span>
            <span style={{ fontSize: 10.5, color: "var(--text-secondary)" }}>(remembers this pharmacy&apos;s layout for next time)</span>
          </label>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <ShieldCheck size={11} weight="bold" /> Image not stored
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onCancel}
            data-testid="rx-scan-review-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--accent btn--sm"
            onClick={handleConfirm}
            data-testid="rx-scan-review-confirm"
          >
            <CheckCircle size={12} weight="bold" /> CONFIRM & CONTINUE
          </button>
        </div>
      </div>
    </div>
  );
}

// Stub re-exports for unused imports to silence the linter without churn.
export const __unused = { Lightning };
